import React, { useEffect } from "react";
import { get, isNil } from "lodash";
import { IconButton } from "@material-ui/core";
import Icon from "@mdi/react";
import { taskColors } from "constants/colors";
import { mdiDotsVertical, mdiClockOutline } from "@mdi/js";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import NameInput from "components/WPReactTable/components/NameInput";
import { updateGroupTask } from "actions/groupTask/updateGroupTask";
import "./styles.scss";

const GroupName = ({ className = "", ...props }) => (
  <div className={`view_KanbanColumn___group-name ${className}`} {...props} data-custom-drag-handle={!props.disableDrag && props.canUpdateProject ? "column-handle" : ""} />
);

const Title = ({ className = "", ...props }) => (
  <div className={`view_KanbanColumn___title ${className}`} {...props} />
);

const ProgressBar = styled.abbr`
  display: block;
  border-bottom: none !important;
  -webkit-text-decoration: none !important;
  text-decoration: none !important;
  height: 5px;
  width: 100%;
  background-color: rgb(228,228,228);
  margin: 2px 0px;
  border-radius: 10px;
  overflow: hidden;
  &::after {
    display: block;
    content: "";
    height: 5px;
    width: ${(props) => props.percent}%;
    background-color: rgb(3, 195, 11);
  }
`;

const Status = ({ className = "", ...props }) => (
  <div className={`view_KanbanColumn___status ${className}`} {...props} />
);

const CodeBox = ({ className = "", ...props }) => (
  <div className={`view_KanbanColumn___code-box ${className}`} {...props} />
);

const Code = ({ className = "", ...props }) => (
  <abbr className={`view_KanbanColumn___code ${className}`} {...props} />
);

const Duration = ({ className = "", ...props }) => (
  <div className={`view_KanbanColumn___duration ${className}`} {...props} />
);

const ManagerBox = ({ className = "", ...props }) => (
  <div className={`view_KanbanColumn___manager-box ${className}`} {...props} />
);

const TextEllipsis = styled.span`
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #1e1f21;
  max-width: 100%;
  display: block;
`;

const StyledHeadingGroup = styled(TextEllipsis)`
  font-size: 16px;
  font-weight: 500;
  line-height: 36px;
  height: 36px;
  margin-left: 0;
  min-width: 1px;
  outline: none;
  overflow: hidden;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const WrapperName = styled.div`
  min-height: 36px;
  font-size: 15px;
  font-weight: 400;
  margin-left: 0;
  min-width: 120px;
  outline: none;
  cursor: pointer;
  padding: 8px;
`;

export function ColumnHeader({
  groupTask,
  index,
  iconButtons = null,
  stageName,
  canUpdateProject,
  triggerEdit = false
}) {
  const gName = get(groupTask, "name", "")
  const { t } = useTranslation();
  const refNameInput = React.useRef(null);
  const dispatch = useDispatch();
  const [groupName, setGroupName] = React.useState(gName);
  const [isEditing, setIsEditing] = React.useState(false);

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (isEditing && refNameInput.current) {
        refNameInput.current?.focus();
        refNameInput.current?.moveToEnd();
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [isEditing]);

  useEffect(() => {
    triggerEdit && setIsEditing(true);
  }, [triggerEdit])

  function _handleKeyPress(e) {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault();
      refNameInput.current.blur();
    }
  }

  function _handleBlur(e) {
    setIsEditing(false);
    if (e.target.value?.trim() && e.target.value.trim() !== groupTask?.name) {
      _handleSubmit(e.target.value.trim());
    } else {
      refNameInput.current.onChange(groupTask?.name);
    }
  }

  function _handleSubmit(name) {
    dispatch(updateGroupTask({ groupTaskId: groupTask?.id, name }));
    setGroupName(name);
    refNameInput.current.onChange(name);
  }

  const _handleEditing = () => {
    if (!canUpdateProject) return;
    setIsEditing(true);
  }

  const renderGroupName = () => {
    if (isEditing) return (
      <NameInput
        ref={refNameInput}
        width="100%"
        maxWidth="calc(100% - 15px)"
        defaultValue={groupName}
        edit={canUpdateProject}
        onKeyPress={_handleKeyPress}
        onBlur={_handleBlur}
        className="kanban-name-group"
        canEdit={canUpdateProject}
      />
    )
    return (
      <WrapperName onClick={_handleEditing}>
        <StyledHeadingGroup>{groupName}</StyledHeadingGroup>
      </WrapperName>
    )
  }

  return (
    <Title>
      <GroupName canUpdateProject={canUpdateProject} disableDrag={isEditing}>
        {renderGroupName()}

        {groupTask.can_modify && !isNil(iconButtons) && (
          <abbr title={t("IDS_WP_MORE")}>
            <IconButton
              size="small"
              aria-controls={`${get(groupTask, "id", "")}-menu`}
              aria-haspopup="true"
              onClick={iconButtons.moreClick}
              className="icon-control-group-task"
            >
              <Icon path={mdiDotsVertical} size={1} color={"#8b8b8b"} />
            </IconButton>
          </abbr>
        )}
      </GroupName>
      <ProgressBar
        title={`${t("IDS_WP_DONE")}: ${get(groupTask, "complete", 0)}%`}
        percent={get(groupTask, "complete", 0)}
        isExpired={get(groupTask, "task_expired", 0) > 0}
      />
      <Status>
        <CodeBox>
          <Code title={`${t("DMH.VIEW.PGP.RIGHT.ALL.STATS.WAITING")}`}>
            <span
              style={{
                color: taskColors[0],
              }}
            >
              &#11044;
            </span>
            <span>{`${get(groupTask, "task_waiting", 0)}`}</span>
          </Code>
          <Code title={`${t("DMH.VIEW.PGP.RIGHT.ALL.STATS.DOING")}`}>
            <span
              style={{
                color: taskColors[1],
              }}
            >
              &#11044;
            </span>
            <span>{`${get(groupTask, "task_doing", 0)}`}</span>
          </Code>
          <Code title={`${t("DMH.VIEW.PGP.RIGHT.ALL.STATS.COMPLETE")}`}>
            <span
              style={{
                color: taskColors[2],
              }}
            >
              &#11044;
            </span>
            <span>{`${get(groupTask, "task_complete", 0)}`}</span>
          </Code>
          <Code title={`${t("DMH.VIEW.PGP.RIGHT.ALL.STATS.EXPIRED")}`}>
            <span
              style={{
                color: taskColors[3],
              }}
            >
              &#11044;
            </span>
            <span>{`${get(groupTask, "task_expired", 0)}`}</span>
          </Code>
          <Code title={`${t("DMH.VIEW.PGP.RIGHT.ALL.STATS.STOP")}`}>
            <span
              style={{
                color: taskColors[4],
              }}
            >
              &#11044;
            </span>
            <span>{`${get(groupTask, "task_stopped", 0)}`}</span>
          </Code>
        </CodeBox>
        <Duration>
          <Icon path={mdiClockOutline} size={0.8} color={"#8b8b8b"} />
          <span>{`${get(groupTask, "duration.value", 0)} ${get(
            groupTask,
            "duration.unit",
            t("IDS_WP_DAY")
          )}`}</span>
        </Duration>
      </Status>
    </Title>
  );
}

export default ColumnHeader;